import { isNativeMobileApp } from '../mobile/platform';

/**
 * FEATURE FLAGS - ETAPA 2 DE MIGRAÇÃO
 * 
 * ⚠️ MUDANÇA NA ETAPA 2:
 * - Backend agora é o PADRÃO (useBackendOrders: true, useBackendMerchant: true)
 * - Supabase direto serve APENAS como fallback automático
 * - Timeout reduzido para 8s (mais agressivo)
 * - Observabilidade aprimorada (logs estruturados)
 * 
 * COMO DESATIVAR O BACKEND (emergência):
 * - Mude useBackendOrders de true para false
 * - Mude useBackendMerchant de true para false
 * - Sistema volta para Supabase direto instantaneamente
 * 
 * ROLLBACK TOTAL (voltar ETAPA 1):
 * - useBackendOrders: false
 * - useBackendMerchant: false
 */

export const FEATURE_FLAGS = {
  /**
   * ETAPA 2: Backend ATIVO por padrão
   * 
   * Usar backend para operações de pedidos (clientes)
   * - createOrder → POST /api/orders/secure (anti-fraude)
   * - getUserOrders → GET /api/orders/my
   * - getOrderById → GET /api/orders/my/{id}
   * 
   * MUDANÇA DA ETAPA 1 → ETAPA 2:
   * - ETAPA 1: false (Supabase direto)
   * - ETAPA 2: true (Backend padrão, Supabase fallback)
   */
  useBackendOrders: true,
  
  /**
   * ETAPA 2: Backend ATIVO por padrão
   * 
   * Usar backend para operações de merchant (logistas)
   * - getMerchantOrders → GET /api/merchant/orders
   * - updateOrderStatus → PATCH /api/orders/{id}/status
   * 
   * MUDANÇA DA ETAPA 1 → ETAPA 2:
   * - ETAPA 1: false (Supabase direto)
   * - ETAPA 2: true (Backend padrão, Supabase fallback)
   */
  useBackendMerchant: true,

  /**
   * Timeout para requisições ao backend (ms)
   * 
   * MUDANÇA DA ETAPA 1 → ETAPA 2:
   * - ETAPA 1: 10000ms (10s) - timeout mais permissivo
   * - ETAPA 2: 8000ms (8s) - timeout mais agressivo
   * 
   * Rationale: Backend deve responder rápido (<500ms p95)
   * Se demorar >8s, melhor usar fallback
   */
  backendTimeout: 15000,

  /**
   * Habilitar fallback automático para Supabase
   * 
   * MANTIDO NA ETAPA 2: true (sempre ativo)
   * 
   * Se true: Em caso de erro no backend, usa Supabase direto
   * Se false: Lança erro sem fallback (NUNCA usar false em produção)
   */
  enableAutoFallback: false,

  /**
   * Tentativas de retry antes de fallback
   * 
   * NOVO NA ETAPA 2
   * 
   * Se backend falhar com erro transitório (500, 502, 503, timeout):
   * - Tenta novamente X vezes com backoff exponencial
   * - Após esgotar tentativas, ativa fallback
   * 
   * Default: 1 (sem retry, vai direto para fallback)
   * Recomendado: 0-2 (evitar loops e latência)
   */
  maxRetries: 1,

  /**
   * Intervalo base para retry (ms)
   * 
   * NOVO NA ETAPA 2
   * 
   * Backoff exponencial:
   * - 1ª tentativa: retryDelay ms
   * - 2ª tentativa: retryDelay * 2 ms
   * - 3ª tentativa: retryDelay * 4 ms
   */
  retryDelay: 500,

  /**
   * Logs detalhados de requisições
   * 
   * ETAPA 2: Logs estruturados com níveis
   * - 'none': Sem logs
   * - 'errors': Apenas erros e fallbacks
   * - 'all': Todos os requests/responses (debug)
   * 
   * MUDANÇA:
   * - ETAPA 1: debugRequests (boolean)
   * - ETAPA 2: logLevel (string com níveis)
   */
  logLevel: 'errors',

  /**
   * Rastreamento de métricas
   * 
   * NOVO NA ETAPA 2
   * 
   * Se true:
   * - Conta requests (backend vs fallback)
   * - Mede tempo de resposta
   * - Conta erros por tipo
   * - Expõe métricas via window.BACKEND_METRICS
   */
  enableMetrics: true
};

/**
 * URL base do backend
 * Em produção, use variável de ambiente
 */
const envBackendUrlRaw = process.env.REACT_APP_BACKEND_URL;
const envMobileBackendUrlRaw = process.env.REACT_APP_MOBILE_BACKEND_URL;
const envBackendUrl = typeof envBackendUrlRaw === 'string' ? envBackendUrlRaw.trim() : '';
const envMobileBackendUrl = typeof envMobileBackendUrlRaw === 'string' ? envMobileBackendUrlRaw.trim() : '';
const isBrowser = typeof window !== 'undefined';
const host = isBrowser ? window.location.hostname : '127.0.0.1';
const protocol = isBrowser ? window.location.protocol : 'http:';
const inferredBackendUrl = `${protocol}//${host}:8000`;
const hostIsLoopback = host === 'localhost' || host === '127.0.0.1';
const envLooksPlaceholder = envBackendUrl.includes('seu_backend');
const envIsLoopback = envBackendUrl.includes('127.0.0.1') || envBackendUrl.includes('localhost');
const hasCustomBackendUrl = envBackendUrl.length > 0 && !envLooksPlaceholder;
const mobileEnvLooksPlaceholder = envMobileBackendUrl.includes('seu_backend');
const hasCustomMobileBackendUrl = envMobileBackendUrl.length > 0 && !mobileEnvLooksPlaceholder;
const shouldUseEnvBackendUrl = hasCustomBackendUrl && (hostIsLoopback || !envIsLoopback);
const runningInsideNativeApp = isNativeMobileApp();

export const BACKEND_URL = runningInsideNativeApp
  ? (hasCustomMobileBackendUrl ? envMobileBackendUrl : (hasCustomBackendUrl ? envBackendUrl : inferredBackendUrl))
  : (shouldUseEnvBackendUrl ? envBackendUrl : inferredBackendUrl);


